import type { PanelVariantName } from './variantMap'

export const FIGMA_PANEL_COLORS = [
  ['Ebony', 23, 23, 24], ['Cast', 54, 54, 54], ['Mineral', 80, 81, 84], ['Armor', 113, 114, 118], ['Nickel', 211, 209, 212], ['Moon', 185, 179, 179], ['Fog', 227, 226, 225],
  ['Zinc', 208, 200, 196], ['Latte', 218, 208, 200], ['Ivory', 225, 203, 179], ['Porcelain', 192, 165, 150], ['Cashmere', 143, 132, 125], ['Ash', 135, 116, 95], ['Timber', 69, 50, 40],
  ['Cocoa', 100, 48, 54], ['Wine', 106, 25, 30], ['Jasper', 119, 61, 45], ['Coral', 171, 103, 70], ['Hot Pink', 226, 102, 64], ['Crimson', 215, 162, 73], ['Ruby', 255, 192, 36],
  ['Scarlet', 162, 166, 75], ['Aries', 162, 161, 112], ['Cinnamon', 150, 145, 127], ['Moab', 116, 109, 82], ['Mustard', 99, 117, 94], ['Dandelion', 101, 145, 127], ['Citrine', 54, 73, 73],
  ['Kiwi', 66, 93, 112], ['Aloe', 33, 40, 63], ['Sage', 46, 64, 99], ['Olive', 74, 79, 100], ['Palm', 127, 142, 177], ['Atlantic', 77, 130, 189], ['Mint', 169, 183, 187],
  ['BabyBlue', 205, 192, 212], ['Periwinkle', 113, 102, 134], ['Cayman', 109, 61, 129], ['SlateBlue', 155, 84, 127], ['Admiral', 214, 103, 133], ['Oxford', 172, 37, 40], ['Indigo', 219, 163, 155],
].map(([label, r, g, b]) => ({ label: label as string, rgb: [r, g, b] as [number, number, number] }))

export type PanelShapeGroupId = 'shapes' | 'wood-slats' | 'other-grids' | 'engraved-tiles' | 'printed-tiles' | 'peak-tiles' | '3d-blocks' | '3d-engraved'
export interface PanelShapeGroupContract {
  id: PanelShapeGroupId
  heading?: string
  label?: string
  unavailable: boolean
  shapeIds: string[]
  rows?: string[][]
  rowLabels?: string[]
  /**
   * Subset of shape IDs within this group that should render as disabled
   * catalog references (e.g. peak tiles inside an otherwise available SHAPES
   * group on hex/90-hex/spaced-hex/45-square/3d, which can't actually host
   * peaks). Only honoured when the group itself is `unavailable: false`.
   */
  unavailableShapeIds?: string[]
}

const squareRows = [
  ['LargeSquare', 'Square', 'Quarter', 'Pixel', 'Bit', 'LargeHalflapVertical', 'ShiplapVertical', 'HalflapVertical', 'MiniShiplapVertical'],
  ['LargeHalfCircleVertical', 'QuarterCircleBottomLeft', 'HalfCircleVertical', 'SmallHalfCircleVertical', 'LeftBottomTriangle', 'Small90LeftBottomTriangle'],
]
// Peak tiles are laid into the right-most column of the LAST row of the SHAPES
// group on every variant, mirroring Figma node 1976-245 / 3368-239200. The
// order matches the Figma mock left-to-right: small → center → upper
// (ascending height). On variants where the grid can't actually host peaks the
// `group()` helper is invoked with `peakRowUnavailable: true` so the buttons
// render as disabled catalog references rather than active selections.
const squarePeakRow = ['SmallPeak', 'LargeCenterPeak', 'LargeUpperPeak']
// 45° peak IDs are appended to the 45-square panel's "Shapes available on
// other grids" disabled row (mirroring Figma node 2850:163599 row 4 of
// 2827:145300-145313) — they share the upright peak icons and are purely
// reference markers.
const fortyFivePeakRow = ['SmallPeak45deg', 'LargeCenterPeak45deg', 'LargeUpperPeak45deg']
const freeformPeakRow = ['SmallPeakFreeform', 'LargeCenterPeakFreeform', 'LargeUpperPeakFreeform']
const hexShapes = ['LargeDiamond', 'SmallDiamond', 'Hex', 'LargeTriangleTop', 'SmallTriangleTop']
const engravedSquare = ['EngravedBackSlash', 'EngravedCross', 'EngravedFourSquare', 'EngravedParallel', 'EngravedTangent', 'EngravedLargeParallel', 'EngravedZen', 'EngravedConverge', 'EngravedOffset', 'EngravedTopo', 'EngravedRiver', 'EngravedSubway', 'EngravedKnot']
const squareEngravedRows = [
  ['EngravedBackSlash', 'EngravedCross', 'EngravedFourSquare', 'EngravedParallel'],
  ['EngravedLargeParallel', 'EngravedTangent', 'EngravedZen', 'EngravedOffset'],
  ['EngravedRiver', 'EngravedTopo', 'EngravedConverge', 'EngravedSubway'],
  ['EngravedKnot'],
]
// The 90° and spaced-hex panels use three authored rows (with a different
// ordering from square), while keeping the full semantic inventory (including
// the unavailable Knot item) for contract/state resolution. Knot is not part
// of the rendered Figma rows.
const hexEngravedRows = [
  ['EngravedBackSlash', 'EngravedCross', 'EngravedFourSquare', 'EngravedParallel'],
  ['EngravedTangent', 'EngravedLargeParallel', 'EngravedZen', 'EngravedConverge'],
  ['EngravedOffset', 'EngravedTopo', 'EngravedRiver', 'EngravedSubway'],
]
const unavailableHexEngraved = (): PanelShapeGroupContract => ({
  id: 'engraved-tiles',
  unavailable: true,
  label: 'Shapes available on other grids',
  rows: hexEngravedRows,
  shapeIds: engravedSquare,
})
const unavailablePrinted = () => group('printed-tiles', printedRows, true, 'Shapes available on other grids')
const unavailableBlocks = () => group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid')
const engravedBlockRows = [
  ['EngravedLargeParallel', 'EngravedTangent', 'EngravedZen'],
  ['EngravedRiver', 'EngravedOffset', 'EngravedTopo'],
  ['EngravedConverge', 'EngravedSubway'],
]
const unavailableEngravedBlocks = () => group('3d-engraved', engravedBlockRows, true, 'Shapes available in 3D Grid', '3D BOXES - ENGRAVED')
const printedRows = [['Aspen', 'Bamboo', 'Geo', 'Pinstripe'], ['Blossom', 'Strand']]
const blockRows = [
  ['Cube36x36x4Set', 'Cube36x36x2Set', 'Cube12x12x4Set', 'Cube12x12x2Set'],
  ['Rect12x36x4Set', 'Rect12x36x2Set'],
]

const group = (
  id: PanelShapeGroupId,
  rows: string[][],
  unavailable = false,
  label?: string,
  heading?: string,
  rowLabels?: string[],
  peakRow?: string[],
  peakRowUnavailable = false,
): PanelShapeGroupContract => {
  // Peak tiles extend the LAST row of the shapes inventory rather than
  // occupying their own row, mirroring Figma node 1976-245 / 3368-239200.
  // On variants where the grid can't actually host peaks the row is tracked
  // as per-shape unavailable so the buttons render disabled but the rest of
  // the group's native shapes stay selectable. On square/freeform the peak
  // row stays available — peakRowUnavailable is left false.
  const fullRows = peakRow && rows.length > 0
    ? [...rows.slice(0, -1), [...rows[rows.length - 1], ...peakRow]]
    : rows
  return {
    id,
    heading,
    label,
    unavailable,
    rows: fullRows,
    rowLabels,
    shapeIds: fullRows.flat(),
    unavailableShapeIds: peakRow && peakRowUnavailable ? [...peakRow] : undefined,
  }
}

export const PANEL_SHAPE_GROUPS: Record<PanelVariantName, PanelShapeGroupContract[]> = {
  square: [
    group('shapes', squareRows, false, undefined, undefined, undefined, squarePeakRow),
    group('wood-slats', [['WoodSlat4Ft', 'WoodSlat8Ft']], false, undefined, 'WOOD SLATS'),
    group('other-grids', [hexShapes], true, 'Shapes available on other grids'),
    group('engraved-tiles', squareEngravedRows),
    group('printed-tiles', printedRows),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  '45-square': [
    // Figma node 2850:163599, context-2850_163599.txt, nodes 2827:145297-145313.
    group('shapes', [
      ['LargeSquare45deg', 'Square45deg', 'Quarter45deg', 'Pixel45deg', 'Bit45deg'],
      ['LargeHalflapVertical45deg', 'ShiplapVertical45deg', 'HalflapVertical45deg', 'MiniShiplapVertical45deg', 'LeftBottomTriangle45deg', 'Small90LeftBottomTriangle45deg'],
      ['LargeHalfCircleVertical45deg', 'QuarterCircleBottomLeft45deg', 'HalfCircleVertical45deg', 'SmallHalfCircleVertical45deg'],
    ]),
    // Figma row 4 (2827:145300-145313) places the disabled peak-tile
    // silhouettes at the right end of the "Shapes available on other grids"
    // row alongside the unavailable hex shapes.
    group('other-grids', [hexShapes], true, 'Shapes available on other grids', undefined, undefined, fortyFivePeakRow, true),
    group('engraved-tiles', [
      ['EngravedBackSlash45deg', 'EngravedCross45deg', 'EngravedFourSquare45deg', 'EngravedParallel45deg'],
      ['EngravedLargeParallel45deg', 'EngravedTangent45deg', 'EngravedZen45deg'],
      ['EngravedOffset45deg', 'EngravedRiver45deg', 'EngravedTopo45deg'],
      ['EngravedConverge45deg', 'EngravedSubway45deg', 'EngravedKnot45deg'],
    ]),
    group('printed-tiles', [['Aspen', 'Bamboo', 'Geo'], ['Pinstripe', 'Blossom', 'Strand']]),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  hex: [
    // Native hex shapes stay available.
    group('shapes', [hexShapes]),
    // Figma node 3368-239200 places the square-grid shapes plus the peak-tile
    // silhouettes at the right end of the last row inside the "Shapes
    // available on other grids" disabled catalog group.
    group('other-grids', squareRows, true, 'Shapes available on other grids', undefined, undefined, squarePeakRow, true),
    group('engraved-tiles', hexEngravedRows, true, 'Shapes available on other grids'),
    group('printed-tiles', printedRows, true, 'Shapes available on other grids'),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  '90-hex': [
    group('shapes', [['Hex90', 'LargeDiamondHorizontal', 'SmallDiamondHorizontal', 'LargeTriangleRight', 'SmallTriangleRight']]),
    group('other-grids', squareRows, true, 'Shapes available on other grids', undefined, undefined, squarePeakRow, true),
    group('engraved-tiles', hexEngravedRows, true, 'Shapes available on other grids'),
    group('printed-tiles', printedRows, true, 'Shapes available on other grids'),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  'spaced-hex': [
    group('shapes', [['HexSpaced', 'SmallDiamondSpaced']]),
    group('other-grids', squareRows, true, 'Shapes available on other grids', undefined, undefined, squarePeakRow, true),
    group('engraved-tiles', hexEngravedRows, true, 'Shapes available on other grids'),
    group('printed-tiles', printedRows, true, 'Shapes available on other grids'),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  freeform: [
    // Figma node 2850:163594, context-2850_163594.txt, nodes 2827:145880-145902.
    group('shapes', [
      ['LargeSquare', 'SquareFreeform', 'Quarter', 'Pixel', 'LargeHalflapVertical', 'ShiplapVertical', 'HalflapVertical', 'MiniShiplapVertical'],
      ['LargeHalfCircleVertical', 'QuarterCircleBottomLeft', 'HalfCircleVertical', 'SmallHalfCircleVertical', 'LeftBottomTriangle', 'Small90LeftBottomTriangle', 'LargeDiamond', 'LargeTriangleTop', 'SmallTriangleTop'],
      ['Hex', 'SmallDiamond'],
    ], false, undefined, undefined, undefined, freeformPeakRow),
    group('wood-slats', [['WoodSlat4FtFreeform', 'WoodSlat8FtFreeform']], false, undefined, 'WOOD SLATS'),
    // Same export, nodes 2827:145908-145919.
    group('engraved-tiles', [
      ['EngravedBackSlashFreeform', 'EngravedCrossFreeform', 'EngravedFourSquareFreeform', 'EngravedParallelFreeform'],
      ['EngravedLargeParallelFreeform', 'EngravedTangentFreeform', 'EngravedZenFreeform', 'EngravedOffsetFreeform'],
      ['EngravedRiverFreeform', 'EngravedSubwayFreeform', 'EngravedConvergeFreeform', 'EngravedTopoFreeform'],
      ['EngravedKnotFreeform'],
    ]),
    group('printed-tiles', [['AspenFreeform', 'BambooFreeform', 'GeoFreeform'], ['PinstripeFreeform', 'BlossomFreeform', 'StrandFreeform']]),
    group('3d-blocks', blockRows, true, 'Shapes available in 3D Grid'),
    unavailableEngravedBlocks(),
  ],
  '3d': [
    group(
      '3d-blocks',
      [
        ['Cube36x36x4Set', 'Cube36x36x2Set'],
        ['Rect12x36x4Set', 'Rect12x36x2Set'],
        ['Cube12x12x4Set', 'Cube12x12x2Set'],
      ],
      false,
      undefined,
      undefined,
      ['(Sold in Sets of 2)', '(Sold in Sets of 4)', '(Sold in Sets of 8)'],
    ),
    // The 3D panel can't host any 2D-grid shape; mirror the hex variant's
    // layout by putting the square shapes + peak silhouettes inside an
    // unavailable "Shapes available on other grids" group. The outer
    // ShapePicker header already renders "SHAPES", so no inner heading is
    // defined here.
    group('other-grids', squareRows, true, 'Shapes available on other grids', undefined, undefined, squarePeakRow, true),
    {
      id: 'engraved-tiles' as PanelShapeGroupId,
      unavailable: true,
      label: 'Shapes available on other grids',
      rows: [
        ['EngravedLargeParallel', 'EngravedTangent', 'EngravedZen', 'EngravedOffset'],
        ['EngravedRiver', 'EngravedTopo', 'EngravedConverge', 'EngravedSubway'],
      ],
      shapeIds: engravedSquare,
    },
    unavailablePrinted(),
  ],
}

export function getPanelShapeState(variant: PanelVariantName, shapeId: string) {
  const owner = PANEL_SHAPE_GROUPS[variant].find(group => group.shapeIds.includes(shapeId))
  if (!owner) throw new Error(`Unknown right-panel semantic shape: ${variant}/${shapeId}`)
  const isPerShapeUnavailable = owner.unavailableShapeIds?.includes(shapeId) ?? false
  return { groupId: owner.id, unavailable: owner.unavailable || isPerShapeUnavailable }
}
